// A player or a game to go with the next chat line
// (PLAN-2026-10-31-players-and-games-in-chat §1): above the box in the
// picker's place, as the stickers are. Player searches the league's players
// by name and, before anything is typed, lists my roster; Game lists this
// week's games, mine first. A tap attaches one and puts the tray away; nothing
// is sent until the person sends it.

import { useState, type KeyboardEvent } from 'react';
import { failureOf, valueOf, type MatchupSummary, type PlayersPage } from '@fsp/domain';
import type { ChatAttachment } from '../data/chat';
import { useMyTeamId, usePlayers, useScoreboard } from '../data/hooks';
import { PositionPill, TeamMark } from '../kit';
import { IconButton, Segments, TextField } from '../kit/ui';
import { clubLabel } from '../sports/registry';

type Tab = 'player' | 'game';

/** A player as the chip over the box says him: *Patrick Mahomes · QB · KC*. */
export function playerLabel(p: {
  name: string;
  positions: readonly string[];
  proTeam: string | null;
}): string {
  return [p.name, p.positions[0], p.proTeam === null ? null : clubLabel(p.proTeam)]
    .filter((x): x is string => typeof x === 'string' && x !== '')
    .join(' · ');
}

/** A game as the chip says it: *Week 8 · Rockets v Monsoon*. */
export function gameLabel(week: string, home: string, away: string): string {
  return `${week} · ${home} v ${away}`;
}

export function ShareTray({
  onPick,
  onClose,
}: {
  onPick: (attachment: ChatAttachment) => void;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<Tab>('player');
  const keys = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Escape') return;
    e.stopPropagation();
    onClose();
  };
  return (
    <div
      className="fsp-stickers fsp-sharetray"
      role="dialog"
      aria-label="Add a player or game"
      onKeyDown={keys}
    >
      <div className="fsp-stickers-head">
        <Segments
          label="Add"
          items={[
            { id: 'player', label: 'Player' },
            { id: 'game', label: 'Game' },
          ]}
          value={tab}
          onChange={setTab}
        />
        <IconButton
          icon="close"
          label="Close players and games"
          variant="quiet"
          onClick={onClose}
        />
      </div>
      {tab === 'player' ? <Players onPick={onPick} /> : <Games onPick={onPick} />}
    </div>
  );
}

function Players({ onPick }: { onPick: (attachment: ChatAttachment) => void }) {
  const [q, setQ] = useState('');
  const typed = q.trim();
  // MY ROSTER UNTIL SOMETHING IS TYPED: the player I talk about most is one
  // tap away. Then the whole league's players by name.
  const read = usePlayers({
    availability: typed === '' ? 'mine' : 'all',
    positions: [],
    q: typed,
    sort: 'average',
    reverse: false,
    cursor: null,
    limit: typed === '' ? 40 : 12,
  });
  const page: PlayersPage | null = valueOf(read.snapshot);
  const failed = page === null ? failureOf(read.snapshot) : null;
  const rows = page?.items ?? [];
  return (
    <div className="fsp-share-body">
      <div className="fsp-share-search">
        <TextField
          label="Find a player"
          hideLabel
          placeholder="Find a player"
          value={q}
          onChange={setQ}
          grow
        />
      </div>
      <div
        className="fsp-share-list"
        role="list"
        aria-label={typed === '' ? 'My players' : 'Players'}
      >
        {failed !== null ? (
          <p className="fsp-share-note">{failed.message}</p>
        ) : page === null ? (
          <p className="fsp-share-note">Looking…</p>
        ) : rows.length === 0 ? (
          <p className="fsp-share-note">
            {typed === '' ? 'Nobody on your roster yet. Type a name.' : 'Nobody by that name.'}
          </p>
        ) : (
          rows.map((r) => {
            const label = playerLabel(r.player);
            return (
              <button
                key={r.player.id}
                type="button"
                role="listitem"
                className="fsp-share-row"
                aria-label={`Add ${label}`}
                onClick={() => onPick({ share: { kind: 'player', playerId: r.player.id }, label })}
              >
                {r.player.positions[0] ? (
                  <PositionPill pos={r.player.positions[0]} size="sm" />
                ) : null}
                <span className="fsp-share-name">{r.player.name}</span>
                <span className="fsp-share-sub">
                  {r.player.proTeam === null ? 'No club' : clubLabel(r.player.proTeam)}
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

function Games({ onPick }: { onPick: (attachment: ChatAttachment) => void }) {
  const board = useScoreboard();
  const view = valueOf(board);
  const failed = view === null ? failureOf(board) : null;
  const myTeamId = useMyTeamId();
  const mine = (m: MatchupSummary) => m.home.team.id === myTeamId || m.away.team.id === myTeamId;
  const games = [...(view?.matchups ?? [])].sort((a, b) => Number(mine(b)) - Number(mine(a)));
  const week = view?.period.label ?? '';
  return (
    <div className="fsp-share-body">
      {week === '' ? null : <p className="fsp-share-week">{`${week}’s games`}</p>}
      <div
        className="fsp-share-list"
        role="list"
        aria-label={week === '' ? 'Games' : `${week}'s games`}
      >
        {failed !== null ? (
          <p className="fsp-share-note">{failed.message}</p>
        ) : view === null ? (
          <p className="fsp-share-note">Looking…</p>
        ) : games.length === 0 ? (
          <p className="fsp-share-note">No games this week.</p>
        ) : (
          games.map((m) => {
            const label = gameLabel(week, m.home.team.name, m.away.team.name);
            return (
              <button
                key={m.id}
                type="button"
                role="listitem"
                className="fsp-share-row fsp-share-game"
                aria-label={`Add ${label}`}
                onClick={() => onPick({ share: { kind: 'matchup', matchupId: m.id }, label })}
              >
                <span className="fsp-share-side">
                  <TeamMark team={m.home.team} size={22} />
                  <span className="fsp-share-name">{m.home.team.name}</span>
                  {m.status === 'pre' ? null : (
                    <span className="fsp-num">{m.home.score.toFixed(1)}</span>
                  )}
                </span>
                <span className="fsp-share-side">
                  <TeamMark team={m.away.team} size={22} />
                  <span className="fsp-share-name">{m.away.team.name}</span>
                  {m.status === 'pre' ? null : (
                    <span className="fsp-num">{m.away.score.toFixed(1)}</span>
                  )}
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
